import { useEffect, useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";

import { EmployeeTableAvatar } from "../../components/features/approve-bank-details/components/employee-table-avatar.tsx";
import { Badge, Button, Modal, Spinner, TextArea } from "../../components/ui/index.ts";
import { showError, showSuccess } from "../../utils/toast.ts";
import type { EnrollmentRequestDetail } from "./project-enrollment-requests-types.ts";
import {
  decideEnrollmentRequest,
  getEnrollmentRequestDetail,
} from "./use-project-enrollment-requests.ts";
import styles from "./project-enrollment-requests-page.module.css";

type Props = {
  requestId: number | null;
  onClose: () => void;
  onUpdated: () => void;
};

export function ProjectEnrollmentRequestDetailModal({ requestId, onClose, onUpdated }: Props) {
  const [detail, setDetail] = useState<EnrollmentRequestDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState("");

  useEffect(() => {
    if (requestId == null) {
      setDetail(null);
      setComment("");
      setCommentError("");
      return;
    }
    let active = true;
    setLoading(true);
    getEnrollmentRequestDetail(requestId)
      .then((value) => {
        if (!active) return;
        setDetail(value);
        setComment(value.reviewComment ?? "");
      })
      .catch((error: unknown) => {
        if (active) showError(error instanceof Error ? error.message : "Failed to load request details.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [requestId]);

  const decide = async (status: "APPROVED" | "REJECTED") => {
    const normalized = comment.trim();
    if (!normalized) {
      setCommentError("A comment is required before approving or rejecting this request.");
      return;
    }
    if (normalized.length > 2000) {
      setCommentError("Comment cannot exceed 2000 characters.");
      return;
    }
    if (requestId == null) return;
    setSaving(true);
    setCommentError("");
    try {
      await decideEnrollmentRequest(requestId, status, normalized);
      showSuccess(`Enrollment request ${status === "APPROVED" ? "approved" : "rejected"}.`);
      onUpdated();
      onClose();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to update the request.");
    } finally {
      setSaving(false);
    }
  };

  const pending = detail?.status === "PENDING";
  return (
    <Modal
      open={requestId != null}
      onClose={saving ? () => undefined : onClose}
      title="Project Enrollment Request"
      size="lg"
      footer={
        detail && pending ? (
          <div className={styles.modalActions}>
            <Button variant="light-grey" disabled={saving} onClick={onClose}>Cancel</Button>
            <Button variant="danger" startIcon={<XCircle size={16} />} disabled={saving} onClick={() => void decide("REJECTED")}>Reject</Button>
            <Button variant="success" startIcon={<CheckCircle2 size={16} />} disabled={saving} onClick={() => void decide("APPROVED")}>{saving ? "Saving..." : "Approve"}</Button>
          </div>
        ) : (
          <Button variant="light-grey" onClick={onClose}>Close</Button>
        )
      }
    >
      {loading || !detail ? (
        <div className={styles.modalLoading}><Spinner size="lg" label="Loading enrollment request" /></div>
      ) : (
        <div className={styles.detailContent}>
          <div className={styles.employeeSummary}>
            <EmployeeTableAvatar employeeId={detail.employeeId} name={detail.employeeName} photoPath={detail.photoPath ?? undefined} size="lg" />
            <div><h3>{detail.employeeName}</h3><p>{detail.designation || "Designation not available"}</p></div>
            <Badge variant={detail.status === "APPROVED" ? "success" : detail.status === "REJECTED" ? "danger" : "warning"} dot>{detail.status.charAt(0) + detail.status.slice(1).toLowerCase()}</Badge>
          </div>
          <section className={styles.detailSection}><h4>Tech Skills</h4><div className={styles.chipList}>{detail.techSkills.length ? detail.techSkills.map((skill) => <Badge key={skill} variant="neutral">{skill}</Badge>) : <span className={styles.muted}>No tech skills available</span>}</div></section>
          <section className={styles.detailSection}><h4>Current Projects</h4><div className={styles.chipList}>{detail.currentProjects.length ? detail.currentProjects.map((project) => <Badge key={project} variant="neutral">{project}</Badge>) : <span className={styles.muted}>No current projects</span>}</div></section>
          <section className={styles.detailSection}><h4>Employee Description</h4><p className={styles.description}>{detail.description}</p></section>
          <TextArea id="enrollment-review-comment" label="Comment" required={pending} disabled={!pending || saving} rows={4} maxLength={2000} showCharacterCount value={comment} error={commentError} placeholder="Add a comment for approval or rejection" onChange={(event) => { setComment(event.target.value); setCommentError(""); }} />
        </div>
      )}
    </Modal>
  );
}
